import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { useNotifications } from "../../../hooks/useNotifications";
import {
  clearNotifications,
  dismissNotification,
  holdNotifications,
  registerNotificationViewport,
} from "../../../services/notify";
import type { NotificationAction } from "../../../services/notify";
import type { ToastStackProps } from "../../molecules/ToastStack";
import { getToastAnchor, getToastAnchorVersion, subscribeToastAnchors } from "../../molecules/ToastAnchor";
import { MAX_VISIBLE, TOAST_AVOID_ATTRIBUTE, TOAST_PHONE_QUERY, TOAST_VIEWPORT_COPY as COPY } from "./ToastViewport.copy";
import { liftFor, splitByPlacement, toStackItem, visibleToasts, withDesignLook } from "./toastViewportModel";

const toastIdOf = (target: EventTarget | null): string | null =>
  target instanceof Element ? target.closest("[data-toast-id]")?.getAttribute("data-toast-id") ?? null : null;

/**
 * The stack's wiring: reads the store, keeps "+N more" expanded or not, pauses timers on hover,
 * focus or a hidden tab, runs actions through the router, dismisses with Escape, moves keyboard
 * focus to a neighbour when a toast goes, and lifts the stack above sticky bars.
 */
export const useToastViewportInternalState = (): ToastStackProps => {
  const snapshot = useNotifications();
  const navigate = useNavigate();
  const isPhone = useMediaQuery(TOAST_PHONE_QUERY);
  const [expanded, setExpanded] = useState(false);
  const [lift, setLift] = useState(0);
  const stackRef = useRef<HTMLElement | null>(null);
  useSyncExternalStore(subscribeToastAnchors, getToastAnchorVersion);
  const { corner, centered } = splitByPlacement(snapshot.toasts.map(withDesignLook), (id) => Boolean(getToastAnchor(id)));
  const { shown, hiddenCount, isExpanded } = visibleToasts(corner, isPhone ? MAX_VISIBLE.phone : MAX_VISIBLE.wide, expanded);

  useLayoutEffect(() => registerNotificationViewport(), []);

  useEffect(() => {
    const onVisibility = () => holdNotifications("hidden", document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      holdNotifications("hidden", false);
      holdNotifications("hover", false);
      holdNotifications("focus", false);
    };
  }, []);

  const hasToasts = snapshot.toasts.length > 0;
  useEffect(() => {
    // The stack unmounts with its last toast, so no pointerleave or blur would release these.
    if (!hasToasts) {
      holdNotifications("hover", false);
      holdNotifications("focus", false);
      setExpanded(false);
    }
  }, [hasToasts]);

  useEffect(() => {
    if (!hasToasts) return undefined;
    const measure = () => {
      const stack = stackRef.current;
      if (!stack) return;
      const rect = stack.getBoundingClientRect();
      const avoid = Array.from(document.querySelectorAll(`[${TOAST_AVOID_ATTRIBUTE}]`))
        .filter((el) => !stack.contains(el))
        .map((el) => el.getBoundingClientRect());
      setLift(liftFor(rect, avoid, window.innerHeight));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, { capture: true });
    };
  }, [hasToasts, snapshot.toasts.length]);

  const dismiss = useCallback((id: string, focusNext = false) => {
    const stack = stackRef.current;
    const ids = Array.from(stack?.querySelectorAll("[data-toast-id]") ?? []).map((el) => el.getAttribute("data-toast-id"));
    const at = ids.indexOf(id);
    dismissNotification(id);
    if (!focusNext) return;
    const neighbour = ids[at + 1] ?? ids[at - 1];
    window.requestAnimationFrame(() => {
      const button = neighbour ? stackRef.current?.querySelector<HTMLButtonElement>(`[data-toast-id="${neighbour}"] button[aria-label^="Dismiss"]`) : null;
      button?.focus({ preventScroll: true });
    });
  }, []);

  const runAction = (action: NotificationAction | null | undefined, id: string) => {
    dismissNotification(id);
    action?.onClick?.();
    if (action?.to) navigate(action.to);
  };

  return {
    label: COPY.label,
    toasts: shown.map(toStackItem),
    hiddenCount,
    expanded: isExpanded,
    moreLabel: COPY.more(hiddenCount),
    moreAriaLabel: COPY.moreAria(hiddenCount),
    fewerLabel: COPY.fewer,
    dismissAllLabel: COPY.dismissAll,
    onToggleExpanded: () => setExpanded((open) => !open),
    onDismissAll: clearNotifications,
    onAction: (id) => runAction(snapshot.toasts.find((toast) => toast.id === id)?.action, id),
    onSecondaryAction: (id) => runAction(snapshot.toasts.find((toast) => toast.id === id)?.secondaryAction, id),
    centered: centered.map((group) => ({ key: group.key, container: getToastAnchor(group.anchor), toasts: group.toasts.map(toStackItem) })),
    onDismiss: (id) => dismiss(id, Boolean(stackRef.current?.contains(document.activeElement))),
    onPointerEnter: () => holdNotifications("hover", true),
    onPointerLeave: () => holdNotifications("hover", false),
    onFocus: () => holdNotifications("focus", true),
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) holdNotifications("focus", false);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const id = toastIdOf(event.target);
      if (!id) return;
      event.preventDefault();
      dismiss(id, true);
    },
    lift,
    polite: snapshot.polite,
    assertive: snapshot.assertive,
    stackRef,
  };
};
